import React, { useState, useEffect } from "react";
import {
  Home,
  ShoppingCart,
  Folder,
  DollarSign,
  MessageCircle,
  Gift,
  Menu,
  X,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import siteLogo from "../assets/icons/logo.png";

export default function Sidebar({ onToggle }) {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("Guest");
  const [balance, setBalance] = useState(0);
  const [loadingUser, setLoadingUser] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  /* ============================================================
     ✅ 1. FIXED — loadUserFromStorage (এটা ব্যালেন্স 0.00 হওয়ার root cause)
     ============================================================ */
  const loadUserFromStorage = () => {
    try {
      const raw = localStorage.getItem("user");

      if (!raw) {
        setUsername("Guest");
        setBalance(0);
        return;
      }

      const stored = JSON.parse(raw);

      setUsername(stored.username || "Guest");
      setBalance(Number(stored.balance || 0));
    } catch (err) {
      console.error("Sidebar user parse error:", err);
      setUsername("Guest");
      setBalance(0);
    } finally {
      setLoadingUser(false);
    }
  };

  /* ============================================================
     ✅ 2. FIXED — Only listen to userUpdated event
        (storage ইভেন্ট remove করা হয়েছে — React SPA তে কাজই করে না)
     ============================================================ */
  useEffect(() => {
    loadUserFromStorage(); // প্রথম লোড

    const sync = () => loadUserFromStorage();
    window.addEventListener("userUpdated", sync);

    return () => {
      window.removeEventListener("userUpdated", sync);
    };
  }, []);

  /* ============================================================
     Sidebar Actions
     ============================================================ */
  const toggleSidebar = () => {
    const next = !open;
    setOpen(next);
    if (typeof onToggle === "function") onToggle(next);
  };

  const handleNewOrderClick = () => {
    navigate("/dashboard", { state: { openNewOrder: true } });
    toggleSidebar();
  };

  const hideSidebarPaths = ["/login", "/register", "/404", "/error"];
  if (hideSidebarPaths.includes(location.pathname)) return null;

  return (
    <div className="z-50 relative">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between bg-[#0b0f1a] px-4 py-3 shadow-md fixed top-0 left-0 right-0 z-40">
        <button
          onClick={toggleSidebar}
          className="text-white text-2xl focus:outline-none transition-transform duration-300 hover:scale-110"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>

        <h1 className="text-lg font-bold italic text-white select-none">
          <span className="text-[#ffd700]">𝒮𝓅𝑒𝑒𝒹</span> 𝐹𝑜𝓁𝓁𝑜𝓌 𝐼𝓃𝒻𝑜.𝒸𝑜𝓂
        </h1>

        <div className="w-6"></div>
      </div>

      {/* Sidebar Drawer */}
      <div
        className={`fixed top-0 left-0 h-full bg-[#111727] shadow-xl transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-500 ease-in-out w-64 z-50`}
      >
        <div className="flex flex-col h-full text-white pt-20">
          {/* 🔰 SIDEBAR PROFILE (LOGO VERSION) */}
<div className="flex flex-col items-center mb-6 mt-2 relative">

  {/* Logo + Online */}
  <div className="relative">
    <img
      src={siteLogo}
      alt="Speed Follow Logo"
      className="w-20 h-20 rounded-full object-cover
      border-2 border-cyan-400
      shadow-[0_0_20px_rgba(0,255,255,0.6)]"
    />

    {/* 🟢 Online Dot */}
    <span
      className="absolute bottom-1 right-1 w-4 h-4 rounded-full 
      bg-green-400 border-2 border-[#111727]
      shadow-[0_0_10px_rgba(0,255,0,0.9)]"
    ></span>
  </div>

  {/* Username + Verified */}
  <div className="mt-3 flex items-center gap-2 font-bold text-lg">
    {loadingUser ? "Loading..." : username}

    <span
      className="flex items-center justify-center w-5 h-5 rounded-full 
      bg-[#1da1f2] shadow-[0_0_8px_#1da1f2]"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="white"
        strokeWidth="3"
        className="w-3 h-3"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  </div>

  {/* Balance */}
  <div className="mt-2 px-4 py-1 rounded-full
    bg-[#f2a900] text-black font-bold text-sm
    shadow-lg">
    ≈ ৳{balance.toFixed(2)}
  </div>
</div>

          {/* Navigation */}
          <nav className="flex-1 px-6 space-y-3">
            <a
              href="/dashboard"
              className="flex items-center gap-3 p-2 hover:bg-[#6c63ff] rounded-md transition"
            >
              <Home /> Dashboard
            </a>

            <button
              onClick={handleNewOrderClick}
              className="w-full text-left flex items-center gap-3 p-2 hover:bg-[#6c63ff] rounded-md transition"
            >
              <ShoppingCart /> New Order
            </button>

            <a
              href="/orders"
              className="flex items-center gap-3 p-2 hover:bg-[#6c63ff] rounded-md transition"
            >
              <Folder /> Orders
            </a>

            <a
              href="/addfunds"
              className="flex items-center gap-3 p-2 hover:bg-[#6c63ff] rounded-md transition"
            >
              <DollarSign /> Add Funds
            </a>

            <a
              href="/support"
              className="flex items-center gap-3 p-2 hover:bg-[#6c63ff] rounded-md transition"
            >
              <MessageCircle /> Support Box
            </a>
            <a
  href="/refer"
  className="flex items-center gap-3 p-2 hover:bg-[#6c63ff] rounded-md transition"
>
  <Gift className="text-pink-400" />
  Refer & Earn
</a>
          </nav>

          <p className="text-sm text-center pb-4 opacity-60">
            © 2025 𝒮𝓅𝑒𝑒𝒹 𝐹𝑜𝓁𝓁𝑜𝓌 𝐼𝓃𝒻𝑜.𝒸𝑜𝓂
          </p>
        </div>
      </div>

      {open && (
        <div
          onClick={toggleSidebar}
          className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm transition-opacity duration-500 z-40"
        ></div>
      )}
    </div>
  );
}